import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import FirmReadinessBoard from "@/pages/admin/FirmReadinessBoard";
import { useAuth } from "@/context/AuthContext";
import { AnalysisExceptionQueue } from "@/components/ops/AnalysisExceptionQueue";

const SEV_TONE = { critical: "text-rose-700 bg-rose-100", high: "text-rose-600 bg-rose-50", medium: "text-amber-600 bg-amber-50", low: "text-slate-500 bg-slate-100" };
const CAT_ICON = { automation: "Workflow", system: "Server", ai: "Sparkles", production: "Factory", finance: "DollarSign" };
const STATUS_TONE = { open: "text-rose-600 bg-rose-50", in_progress: "text-blue-600 bg-blue-50", resolved: "text-emerald-600 bg-emerald-50" };
const fmtDT = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function Stat({ label, value, tone = "text-[#171B2E]" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={cn("text-xl font-heading font-bold mt-0.5", tone)}>{value}</div>
    </div>
  );
}

function DetailDialog({ exc, onClose, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  if (!exc) return null;
  const act = async (fn, key) => { setBusy(key); try { await fn(); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(""); } };
  const retry = () => act(async () => { await api.post(`/operations/exceptions/${exc.id}/retry`); toast.success("Retry succeeded — resolved"); onClose(); }, "retry");
  const resolve = () => act(async () => { await api.post(`/operations/exceptions/${exc.id}/resolve`); toast.success("Marked resolved"); onClose(); }, "resolve");
  const addNote = () => act(async () => { if (!note.trim()) return; await api.post(`/operations/exceptions/${exc.id}/note`, { note }); setNote(""); toast.success("Note added"); }, "note");
  const retriage = () => act(async () => { await api.post(`/operations/exceptions/${exc.id}/triage`); toast.success("Allison re-triaged"); }, "triage");
  return (
    <Dialog open={!!exc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ops-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><span className={cn("text-[11px] px-2 py-0.5 rounded-full", SEV_TONE[exc.severity])}>{exc.severity}</span>{exc.title}</DialogTitle>
          <DialogDescription>{exc.component} · {cap(exc.category)} · {fmtDT(exc.created_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {exc.client_name && <div><span className="text-slate-400 text-xs">Client:</span> {exc.client_name}</div>}
          {exc.automation && <div><span className="text-slate-400 text-xs">Automation:</span> {exc.automation}</div>}
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-xs text-rose-700 font-mono break-words">{exc.error}</div>
          {exc.suggested_resolution && <div className="text-xs text-slate-500"><span className="font-semibold">Suggested:</span> {exc.suggested_resolution}</div>}
          {exc.allison_triage && (
            <div className="rounded-lg border border-fuchsia-100 bg-fuchsia-50/50 p-3 space-y-1" data-testid="ops-triage">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-700"><Icons.Sparkles className="h-3.5 w-3.5" />Allison Triage <span className="text-[10px] font-normal text-fuchsia-400">advisory</span></div>
              <div className="text-xs text-slate-600"><span className="font-semibold">Likely cause:</span> {exc.allison_triage.likely_cause}</div>
              <div className="text-xs text-slate-600"><span className="font-semibold">Recommended fix:</span> {exc.allison_triage.recommended_fix}</div>
              {exc.allison_triage.explanation && <p className="text-[11px] text-slate-500 whitespace-pre-wrap">{exc.allison_triage.explanation}</p>}
              <div className="text-[10px] text-fuchsia-400">Confidence {Math.round((exc.allison_triage.confidence || 0) * 100)}% · {exc.allison_triage.source === "llm" ? "Allison (Claude)" : "heuristic"}</div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {exc.retryable && exc.status !== "resolved" && (
              <button onClick={retry} disabled={busy} data-testid="ops-retry" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60">
                {busy === "retry" ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.RefreshCw className="h-4 w-4" />}Retry / Regenerate
              </button>
            )}
            {exc.status !== "resolved" && <button onClick={resolve} disabled={busy} data-testid="ops-resolve" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><Icons.CheckCircle2 className="h-4 w-4" />Mark Resolved</button>}
            <button onClick={retriage} disabled={busy} data-testid="ops-retriage" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-fuchsia-200 text-fuchsia-700 text-sm hover:bg-fuchsia-50">{busy === "triage" ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Sparkles className="h-4 w-4" />}Re-triage</button>
          </div>
          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-semibold text-slate-400 mb-1">Notes</div>
            {(exc.notes || []).map((n, i) => <div key={i} className="text-xs text-slate-500 mb-1">{n.text} <span className="text-slate-300">· {n.by} · {fmtDT(n.at)}</span></div>)}
            <div className="flex gap-2 mt-1">
              <input className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="ops-note-input" />
              <button onClick={addNote} disabled={busy} className="h-8 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">Add</button>
            </div>
          </div>
          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-semibold text-slate-400 mb-1">Audit</div>
            {(exc.audit || []).map((a, i) => <div key={i} className="text-[11px] text-slate-400">{cap(a.action)} — {a.detail} · {a.actor} · {fmtDT(a.at)}</div>)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Zoom Recovery Panel ────────────────────────────────────────────────────

const FROM_DATE = "2026-01-01";
const todayStr = () => new Date().toISOString().slice(0, 10);

const HEALTH_STATE_TONE = {
  healthy: "text-emerald-700 bg-emerald-50 border-emerald-200",
  scope_missing: "text-amber-700 bg-amber-50 border-amber-200",
  stale_analyses: "text-orange-700 bg-orange-50 border-orange-200",
  not_configured: "text-rose-700 bg-rose-50 border-rose-200",
};

function RecoveryStatCard({ label, value, tone = "text-[#171B2E]", sub }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 min-w-[100px]">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={cn("text-2xl font-heading font-bold mt-0.5", tone)}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function JsonBlock({ data, id, expandedJson, setExpandedJson }) {
  const open = expandedJson[id];
  return (
    <div className="mt-3">
      <button
        onClick={() => setExpandedJson(prev => ({ ...prev, [id]: !prev[id] }))}
        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        data-testid={`zoom-json-toggle-${id}`}
      >
        <Icons.Code2 className="h-3.5 w-3.5" />
        {open ? "Hide" : "Show"} full JSON response
      </button>
      {open && (
        <pre className="mt-2 bg-slate-950 text-emerald-300 text-[11px] font-mono rounded-xl p-4 overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre-wrap break-words" data-testid={`zoom-json-${id}`}>
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function HealthSummary({ data }) {
  const wm = data.integration_health || {};
  const tone = HEALTH_STATE_TONE[data.health_state] || "text-slate-600 bg-slate-50 border-slate-200";
  return (
    <div className="space-y-3" data-testid="zoom-health-summary">
      <div className="flex flex-wrap gap-2 items-center">
        <span className={cn("text-xs font-semibold px-3 py-1 rounded-full border", tone)}>
          {cap(data.health_state || "unknown")}
        </span>
        {data.zoom_configured ? (
          <span className="text-[11px] text-emerald-600 flex items-center gap-1"><Icons.Check className="h-3.5 w-3.5" />Zoom configured</span>
        ) : (
          <span className="text-[11px] text-rose-600 flex items-center gap-1"><Icons.X className="h-3.5 w-3.5" />Zoom not configured</span>
        )}
        {data.webhook_secret_configured ? (
          <span className="text-[11px] text-emerald-600 flex items-center gap-1"><Icons.Check className="h-3.5 w-3.5" />Webhook secret set</span>
        ) : (
          <span className="text-[11px] text-rose-600 flex items-center gap-1"><Icons.X className="h-3.5 w-3.5" />Webhook secret missing</span>
        )}
        {data.recording_scope_available === true && (
          <span className="text-[11px] text-emerald-600 flex items-center gap-1"><Icons.Check className="h-3.5 w-3.5" />Recording scope OK</span>
        )}
        {data.recording_scope_available === false && (
          <span className="text-[11px] text-amber-600 flex items-center gap-1"><Icons.AlertTriangle className="h-3.5 w-3.5" />Scope missing</span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <RecoveryStatCard label="Stale Analyses" value={data.stale_analyses_count} tone={data.stale_analyses_count > 0 ? "text-orange-600" : "text-emerald-600"} />
        <RecoveryStatCard label="Recoverable" value={data.recoverable_analyses_count} tone={data.recoverable_analyses_count > 0 ? "text-amber-600" : "text-emerald-600"} />
        <RecoveryStatCard label="Last Webhook" value={data.last_webhook_at ? fmtDT(data.last_webhook_at) : "None"} tone="text-slate-600" />
        <RecoveryStatCard label="Last Transcript Import" value={data.last_transcript_import_at ? fmtDT(data.last_transcript_import_at) : "None"} tone="text-slate-600" />
      </div>
      {wm.last_sweep_completed_at && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 space-y-1">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Auto-Sweep Watermark</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-600">
            <div><span className="text-slate-400">Last completed:</span> {fmtDT(wm.last_sweep_completed_at)}</div>
            <div><span className="text-slate-400">Last found:</span> {wm.last_sweep_found ?? "—"}</div>
            <div><span className="text-slate-400">Last imported:</span> {wm.last_sweep_imported ?? "—"}</div>
            <div><span className="text-slate-400">Already present:</span> {wm.last_sweep_already_present ?? "—"}</div>
            <div><span className="text-slate-400">Skipped:</span> {wm.last_sweep_skipped ?? "—"}</div>
            <div><span className="text-slate-400">Failed:</span> {wm.last_sweep_failed ?? "—"}</div>
          </div>
        </div>
      )}
      {data.zoom_api_error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-xl p-3 font-mono">{data.zoom_api_error}</div>
      )}
    </div>
  );
}

function RepairDrySummary({ data }) {
  const toRepair = data.transcripts_to_repair || [];
  return (
    <div className="space-y-3" data-testid="zoom-repair-dry-summary">
      <div className="grid grid-cols-3 gap-2">
        <RecoveryStatCard label="Inspected" value={data.inspected} />
        <RecoveryStatCard label="Would Repair" value={data.would_repair} tone={data.would_repair > 0 ? "text-amber-600" : "text-emerald-600"} />
        <RecoveryStatCard label="Would Skip" value={data.would_skip} tone="text-slate-600" />
      </div>
      {toRepair.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Transcripts to repair ({toRepair.length})</div>
          <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
            {toRepair.map((t, i) => (
              <div key={i} className="px-3 py-2 text-xs flex justify-between gap-2">
                <span className="font-mono text-slate-500">{t.id?.slice(0, 8)}…</span>
                <span className="flex-1 truncate">{t.title || "Untitled"}</span>
                <span className="text-amber-600 shrink-0">{t.current_client_id ? "host-attributed" : "unmatched"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RepairLiveSummary({ data }) {
  return (
    <div className="space-y-3" data-testid="zoom-repair-live-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <RecoveryStatCard label="Inspected" value={data.inspected} />
        <RecoveryStatCard label="Repaired" value={data.repaired} tone={data.repaired > 0 ? "text-emerald-600" : "text-slate-600"} />
        <RecoveryStatCard label="Re-queued" value={data.re_queued} tone="text-blue-600" />
        <RecoveryStatCard label="Skipped" value={data.skipped} tone="text-slate-500" />
      </div>
      {(data.errors || []).length > 0 && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-xl p-3 space-y-1">
          <div className="font-semibold">{data.errors.length} error(s)</div>
          {data.errors.map((e, i) => <div key={i} className="font-mono">{e.id}: {e.error}</div>)}
        </div>
      )}
      {data.note && <p className="text-xs text-slate-500 italic">{data.note}</p>}
    </div>
  );
}

function RecoverySummaryBlock({ data, label }) {
  const s = data.summary || {};
  const outcomes = [
    ["Recordings Found", data.recordings_found],
    ["Already Ingested", s.already_ingested, s.already_ingested > 0 ? "text-slate-500" : undefined],
    ["Retried (stuck)", s.retried, s.retried > 0 ? "text-blue-600" : undefined],
    ["Imported / Would Import", s.imported, s.imported > 0 ? "text-emerald-600" : undefined],
    ["No Transcript (VTT)", s.transcript_not_ready, s.transcript_not_ready > 0 ? "text-amber-600" : undefined],
    ["Scope Unavailable", s.scope_unavailable, s.scope_unavailable > 0 ? "text-rose-600" : undefined],
    ["API Failures", s.api_failure, s.api_failure > 0 ? "text-rose-600" : undefined],
  ];
  return (
    <div className="space-y-3" data-testid={`zoom-recovery-${label}-summary`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {outcomes.filter(([, v]) => v != null && v !== undefined).map(([lbl, val, tone]) => (
          <RecoveryStatCard key={lbl} label={lbl} value={val} tone={tone || "text-[#171B2E]"} />
        ))}
      </div>
      {data.note && <p className="text-xs text-slate-500 italic">{data.note}</p>}
      {data.scope_available === false && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <strong>Scope missing:</strong> Add <code>recording:read:admin</code> in Zoom Marketplace → your S2S app → Scopes, then rerun.
        </div>
      )}
    </div>
  );
}

function ResultSection({ title, icon: Icon, result, summaryComp: SummaryComp, summaryProps, id, expandedJson, setExpandedJson, tag }) {
  if (!result) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`zoom-result-${id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          <span className="font-semibold text-sm text-[#171B2E]">{title}</span>
        </div>
        {tag && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{tag}</span>}
      </div>
      <SummaryComp data={result} {...(summaryProps || {})} />
      <JsonBlock data={result} id={id} expandedJson={expandedJson} setExpandedJson={setExpandedJson} />
    </div>
  );
}

function ZoomRecoveryPanel() {
  const { user } = useAuth();
  const canAccess = ["administrator", "partner", "manager"].includes(user?.role);

  const [healthResult, setHealthResult] = useState(null);
  const [repairDryResult, setRepairDryResult] = useState(null);
  const [recoveryDryResult, setRecoveryDryResult] = useState(null);
  const [repairLiveResult, setRepairLiveResult] = useState(null);
  const [recoveryLiveResult, setRecoveryLiveResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmOp, setConfirmOp] = useState(null);
  const [expandedJson, setExpandedJson] = useState({});

  if (!canAccess) return null;

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); }
    catch (e) { toast.error(e.response?.data?.detail || `${key} failed`); }
    finally { setBusy(null); }
  };

  const runHealth = () => run("health", async () => {
    const r = await api.get("/operations/zoom-health");
    setHealthResult(r.data);
  });

  const runRepairDry = () => run("repair_dry", async () => {
    const r = await api.post("/operations/zoom-repair", { dry_run: true, from_date: FROM_DATE, to_date: todayStr() });
    setRepairDryResult(r.data);
    toast.info(`Repair dry-run: ${r.data.would_repair} would be repaired`);
  });

  const runRecoveryDry = () => run("recovery_dry", async () => {
    const r = await api.post("/operations/zoom-recovery", { dry_run: true, from_date: FROM_DATE, to_date: todayStr() });
    setRecoveryDryResult(r.data);
    toast.info(`Recovery dry-run: ${r.data.recordings_found ?? 0} recordings found`);
  });

  const commitRepairLive = () => run("repair_live", async () => {
    const r = await api.post("/operations/zoom-repair", { dry_run: false, from_date: FROM_DATE, to_date: todayStr() });
    setRepairLiveResult(r.data);
    toast.success(`Repair committed: ${r.data.repaired} repaired, ${r.data.re_queued} re-queued`);
  });

  const commitRecoveryLive = () => run("recovery_live", async () => {
    const r = await api.post("/operations/zoom-recovery", { dry_run: false, from_date: FROM_DATE, to_date: todayStr() });
    setRecoveryLiveResult(r.data);
    toast.success(`Recovery committed: ${r.data.summary?.imported ?? 0} imported`);
  });

  const Btn = ({ id, icon: Icon, label, onClick, disabled, variant = "default", testId }) => {
    const base = "inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors";
    const styles = {
      default: "bg-[#171B2E] text-white hover:bg-slate-800",
      outline: "border border-slate-200 text-slate-700 bg-white hover:bg-slate-50",
      danger: "bg-rose-600 text-white hover:bg-rose-700",
      amber: "bg-amber-500 text-white hover:bg-amber-600",
    };
    const isBusy = busy === id;
    return (
      <button onClick={onClick} disabled={disabled || !!busy} className={cn(base, styles[variant])} data-testid={testId || `zoom-btn-${id}`}>
        {isBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
        {label}
      </button>
    );
  };

  return (
    <div className="pt-2 space-y-4" data-testid="zoom-recovery-panel">
      {/* Header */}
      <div>
        <h2 className="font-heading text-lg font-bold text-[#171B2E] flex items-center gap-2">
          <Icons.Video className="h-5 w-5 text-blue-600" />
          Zoom Pipeline P0 — Production Verification
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Code complete · Production verification pending. Run each step in order.
          Historical range: <strong>{FROM_DATE}</strong> → <strong className="text-blue-700">today (computed at runtime)</strong>
        </p>
      </div>

      {/* Action buttons */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Step 1 — Diagnostics</div>
        <div className="flex flex-wrap gap-2">
          <Btn id="health" icon={Icons.HeartPulse} label="Check Zoom Health" onClick={runHealth} variant="default" testId="zoom-btn-health" />
          <Btn id="repair_dry" icon={Icons.Search} label="Repair Dry-Run" onClick={runRepairDry} variant="outline" testId="zoom-btn-repair-dry" />
          <Btn id="recovery_dry" icon={Icons.ListChecks} label="Recovery Dry-Run" onClick={runRecoveryDry} variant="outline" testId="zoom-btn-recovery-dry" />
        </div>

        <div className="border-t border-slate-200 pt-3">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Step 2 — Live Operations (confirm required)</div>
          <div className="flex flex-wrap gap-2">
            <Btn
              id="repair_live"
              icon={Icons.Wrench}
              label="Run Live Repair"
              onClick={() => setConfirmOp("repair_live")}
              disabled={!repairDryResult || !!repairLiveResult}
              variant="amber"
              testId="zoom-btn-repair-live"
            />
            <Btn
              id="recovery_live"
              icon={Icons.Download}
              label="Run Live Historical Recovery"
              onClick={() => setConfirmOp("recovery_live")}
              disabled={!recoveryDryResult || !!recoveryLiveResult}
              variant="amber"
              testId="zoom-btn-recovery-live"
            />
          </div>
          {!repairDryResult && <p className="text-[11px] text-slate-400 mt-1">Run Repair Dry-Run first to enable Live Repair.</p>}
          {!recoveryDryResult && <p className="text-[11px] text-slate-400 mt-0.5">Run Recovery Dry-Run first to enable Live Historical Recovery.</p>}
          {repairLiveResult && <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><Icons.Check className="h-3.5 w-3.5" />Live Repair already completed this session.</p>}
          {recoveryLiveResult && <p className="text-[11px] text-emerald-600 mt-0.5 flex items-center gap-1"><Icons.Check className="h-3.5 w-3.5" />Live Recovery already completed this session.</p>}
        </div>
      </div>

      {/* Results */}
      <ResultSection
        title="Zoom Health" icon={Icons.HeartPulse} result={healthResult}
        summaryComp={HealthSummary} id="health"
        expandedJson={expandedJson} setExpandedJson={setExpandedJson}
        tag={healthResult ? cap(healthResult.health_state || "") : null}
      />
      <ResultSection
        title="Repair Dry-Run" icon={Icons.Search} result={repairDryResult}
        summaryComp={RepairDrySummary} id="repair_dry"
        expandedJson={expandedJson} setExpandedJson={setExpandedJson}
        tag={repairDryResult ? `${repairDryResult.would_repair} would repair` : null}
      />
      <ResultSection
        title="Recovery Dry-Run" icon={Icons.ListChecks} result={recoveryDryResult}
        summaryComp={RecoverySummaryBlock} summaryProps={{ label: "dry" }}
        id="recovery_dry"
        expandedJson={expandedJson} setExpandedJson={setExpandedJson}
        tag={recoveryDryResult ? `${recoveryDryResult.recordings_found ?? 0} recordings found` : null}
      />
      <ResultSection
        title="Live Repair — Committed" icon={Icons.Wrench} result={repairLiveResult}
        summaryComp={RepairLiveSummary} id="repair_live"
        expandedJson={expandedJson} setExpandedJson={setExpandedJson}
        tag="LIVE"
      />
      <ResultSection
        title="Live Recovery — Committed" icon={Icons.Download} result={recoveryLiveResult}
        summaryComp={RecoverySummaryBlock} summaryProps={{ label: "live" }}
        id="recovery_live"
        expandedJson={expandedJson} setExpandedJson={setExpandedJson}
        tag="LIVE"
      />

      {/* Confirmation dialog */}
      <Dialog open={!!confirmOp} onOpenChange={(o) => !o && setConfirmOp(null)}>
        <DialogContent className="max-w-md" data-testid="zoom-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.AlertTriangle className="h-5 w-5 text-amber-500" />
              {confirmOp === "repair_live" ? "Confirm Live Repair" : "Confirm Live Historical Recovery"}
            </DialogTitle>
            <DialogDescription>
              {confirmOp === "repair_live"
                ? `This will clear host misattribution on all affected Zoom transcripts and re-queue them for correct identity resolution. Range: ${FROM_DATE} → today. This action is logged and audited.`
                : `This will download and import all eligible Zoom recordings from ${FROM_DATE} to today into Tax Flow. New transcripts will be analyzed by Allison. This action is logged and audited.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 justify-end pt-2">
            <button onClick={() => setConfirmOp(null)} className="h-9 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50" data-testid="zoom-confirm-cancel">
              Cancel
            </button>
            <button
              onClick={() => {
                const op = confirmOp;
                setConfirmOp(null);
                if (op === "repair_live") commitRepairLive();
                else commitRecoveryLive();
              }}
              className="h-9 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold flex items-center gap-2"
              data-testid="zoom-confirm-proceed"
            >
              <Icons.Check className="h-4 w-4" />
              Proceed
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Zoom Enrichment Panel ───────────────────────────────────────────────────

function ZoomEnrichmentPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/operations/zoom-enrichment-diagnostics");
        setData(r.data);
      } catch { /* non-blocking */ } finally { setLoading(false); }
    })();
  }, []);
  if (loading || !data) return null;
  const items = data.items || [];
  return (
    <div className="pt-2" data-testid="ops-zoom-enrichment">
      <h2 className="font-heading text-lg font-bold text-[#171B2E] flex items-center gap-2"><Icons.Video className="h-5 w-5" />Zoom Enrichment</h2>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Meetings where Zoom could not attach invitee emails or metadata. Usually a missing Marketplace read scope on the Server-to-Server OAuth app.</p>
      {items.length === 0 ? (
        <div className="text-center py-10 text-slate-400 bg-white border border-slate-200 rounded-xl" data-testid="ops-zoom-empty">
          <Icons.ShieldCheck className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
          <p className="text-sm">All clear — no Zoom enrichment failures.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="ops-zoom-list">
          {items.map((z) => (
            <div key={z.id} data-testid={`ops-zoom-row-${z.id}`} className="w-full bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start gap-3">
              <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 text-amber-600 bg-amber-50"><Icons.VideoOff className="h-4 w-4" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#171B2E] text-sm truncate">{z.title}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full text-amber-700 bg-amber-50">{cap(z.enrichment_status || "failed")}</span>
                  {z.client_id ? <span className="text-[11px] px-2 py-0.5 rounded-full text-emerald-600 bg-emerald-50">Lead created</span> : null}
                </div>
                <div className="text-xs text-slate-400 mt-1">Meeting {z.zoom_meeting_id || "—"} · {fmtDT(z.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OperationsCenter() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ category: "all", severity: "all", status: "all", q: "" });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
      const [s, l] = await Promise.all([api.get("/operations/summary"), api.get("/operations/exceptions", { params })]);
      setSummary(s.data); setItems(l.data.items || []);
    } catch { toast.error("Could not load Operations Center"); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const inp = "border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200";
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="operations-center-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Operations Center</h1>
        <p className="text-sm text-slate-500 mt-1">Mission control for firm operations. Engagement readiness drives every downstream decision — followed by automation exceptions, which never fail silently.</p>
      </div>

      <FirmReadinessBoard />

      <div className="pt-1">
        <h2 className="font-heading text-lg font-bold text-[#171B2E] flex items-center gap-2"><Icons.AlertTriangle className="h-5 w-5" />Automation Exceptions</h2>
        <p className="text-xs text-slate-500 mt-0.5">Every failed automation becomes an actionable, recoverable work item.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          <Stat label="Open" value={summary.open} tone="text-rose-600" />
          <Stat label="Total" value={summary.total} />
          {Object.entries(summary.by_category).map(([k, v]) => <Stat key={k} label={cap(k)} value={v} />)}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input className={cn(inp, "w-full pl-9")} placeholder="Search exceptions…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="ops-search" />
        </div>
        <select className={inp} value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} data-testid="ops-filter-cat">
          <option value="all">All categories</option>{["automation", "system", "ai", "production", "finance"].map((c) => <option key={c} value={c}>{cap(c)}</option>)}
        </select>
        <select className={inp} value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))} data-testid="ops-filter-sev">
          <option value="all">All severities</option>{["critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{cap(s)}</option>)}
        </select>
        <select className={inp} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="ops-filter-status">
          <option value="all">All statuses</option>{["open", "in_progress", "resolved"].map((s) => <option key={s} value={s}>{cap(s)}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-16"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        : items.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="ops-empty">
            <Icons.ShieldCheck className="h-10 w-10 mx-auto mb-3 text-emerald-400" />
            <p>All clear — no automation exceptions.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((e) => {
              const I = Icons[CAT_ICON[e.category]] || Icons.AlertTriangle;
              return (
                <button key={e.id} onClick={() => setDetail(e)} data-testid={`ops-row-${e.id}`} className="w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start gap-3 hover:border-slate-300">
                  <span className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0", SEV_TONE[e.severity])}><I className="h-4 w-4" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#171B2E] text-sm">{e.title}</span>
                      <span className={cn("text-[11px] px-2 py-0.5 rounded-full", STATUS_TONE[e.status])}>{cap(e.status)}</span>
                      {e.retryable && <span className="text-[11px] text-blue-600 inline-flex items-center gap-0.5"><Icons.RefreshCw className="h-3 w-3" />Retryable</span>}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">{e.component} · {cap(e.category)}{e.client_name ? ` · ${e.client_name}` : ""} · {fmtDT(e.created_at)}</div>
                  </div>
                  <Icons.ChevronRight className="h-4 w-4 text-slate-300 mt-1" />
                </button>
              );
            })}
          </div>
        )}

      <DetailDialog exc={detail} onClose={() => setDetail(null)} onChanged={load} />

      {/* Analysis Exception Management */}
      <div className="pt-1 border-t border-slate-100" data-testid="ops-analysis-exceptions-section">
        <AnalysisExceptionQueue mode="management" />
      </div>

      <ZoomRecoveryPanel />

      <ZoomEnrichmentPanel />
    </div>
  );
}
